import type { PricingBreakdown } from '@data/publicPlans';

export function getMaterialsPlusLaborTotal(
	pb: { materials?: { total?: number }[]; labor?: { total?: number }[] } | undefined
): number {
	if (!pb) return 0;
	const materials = (pb.materials ?? []).reduce((sum, m) => sum + (Number(m.total) || 0), 0);
	const labor = (pb.labor ?? []).reduce((sum, l) => sum + (Number(l.total) || 0), 0);
	return Math.round(materials + labor);
}

export function defaultPricingBreakdown(): PricingBreakdown {
	return { min: 0, mid: 0, max: 0, materials: [], labor: [] };
}

function sanitizePricingLineItems(pb: PricingBreakdown): PricingBreakdown {
	const materials = (Array.isArray(pb.materials) ? pb.materials : [])
		.map((m) => {
			const quantity = Number(m?.quantity ?? 0);
			const unitPrice = Number(m?.unitPrice ?? 0);
			const total = Number(m?.total ?? 0) || quantity * unitPrice;
			return {
				name: String(m?.name ?? '').trim(),
				quantity,
				unit: String(m?.unit ?? ''),
				unitPrice,
				total
			};
		})
		.filter((m) => m.name);

	const labor = (Array.isArray(pb.labor) ? pb.labor : [])
		.map((l) => {
			const hours = Number(l?.hours ?? 0);
			const ratePerHour = Number(l?.ratePerHour ?? 0);
			const total = Number(l?.total ?? 0) || hours * ratePerHour;
			return {
				name: String(l?.name ?? '').trim(),
				hours,
				ratePerHour,
				total
			};
		})
		.filter((l) => l.name);

	return { ...pb, materials, labor };
}

/**
 * Mid = sum of itemized materials + labor when line items exist (most likely estimate).
 * Min/max stay as outer bands when provided; otherwise derived from mid.
 */
export function normalizePricingBreakdown(pb: PricingBreakdown | undefined, midOverride?: number): PricingBreakdown {
	const base = sanitizePricingLineItems(pb ?? defaultPricingBreakdown());
	const lineTotal = getMaterialsPlusLaborTotal(base);

	const storedMid = Math.round(Number(base.mid ?? 0));
	const mid =
		lineTotal > 0
			? lineTotal
			: Math.max(0, Math.round(midOverride ?? storedMid));

	const storedMin = Math.round(Number(base.min ?? 0));
	const min =
		lineTotal > 0
			? storedMin > 0 && storedMin <= mid
				? storedMin
				: Math.round(mid * 0.85)
			: Math.max(0, storedMin);

	const storedMax = Math.round(Number(base.max ?? 0));
	const max = storedMax >= mid ? storedMax : Math.round(mid * 1.15);

	return { ...base, min, mid, max };
}
